/**
 * Inventory family — shared, UI-free rules (status language, locations, movement documents).
 * Every tab uses these so "Low", "Ran out", a GRN document or a location label mean the same
 * thing on the Dashboard, Stock, Sites BOQ, Catalog and Movements tabs.
 */
import { ageDays, formatDate, formatNumber, parseDateValue } from '@/lib/format';

export const INVENTORY_LOCATIONS = [
  'Abu Dhabi Store',
  'Abu Dhabi Office',
  'Dubai Store',
  'Dubai Workshop',
  'Dubai Office Lab'
];

/** Short labels for tight cells + calm colours for bars/legends (no red: red means a problem). */
export const LOCATION_META = {
  'Abu Dhabi Store': { short: 'AD Store', color: 'var(--navy-600)' },
  'Dubai Store': { short: 'DXB Store', color: 'var(--status-blue)' },
  'Abu Dhabi Office': { short: 'AD Office', color: 'var(--status-teal)' },
  'Dubai Workshop': { short: 'DXB Workshop', color: 'var(--status-violet)' },
  'Dubai Office Lab': { short: 'DXB Lab', color: 'var(--ink-4)' }
};

export const locationShort = (location) => LOCATION_META[location]?.short || location || '—';
export const locationColor = (location) => LOCATION_META[location]?.color || 'var(--ink-4)';

/** Items without a Min level count as low below this. */
export const DEFAULT_MIN_LEVEL = 5;

export const toQty = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export const formatQty = (value) => formatNumber(toQty(value), { maxDecimals: 2 });

/** The server's 400 detail for a part number that fails isValidPartNumber (routes/inventory.py PART_NUMBER_INVALID). */
export const PART_NUMBER_INVALID = 'Enter a real part number — letters and digits, e.g. MTN6705-0008';

/**
 * Same rule as is_valid_part_number() in backend/routes/inventory.py: '-' or '10"' are data-entry
 * slips (no letters/digits, a single character, or a bare number under 4 digits); 'PC' or 'AFS' pass.
 */
export function isValidPartNumber(value) {
  const alnum = String(value ?? '').replace(/[^A-Za-z0-9]/g, '');
  if (alnum.length < 2) return false;
  if (/^\d+$/.test(alnum) && alnum.length < 4) return false;
  return true;
}

/** Data-quality warning shown under the part number, or null when the record is fine. */
export function itemDataIssue(item) {
  const partNumber = String(item?.part_number ?? '');
  const hasDescription = Boolean(String(item?.description ?? '').trim());
  if (!partNumber.replace(/[^A-Za-z0-9]/g, '')) {
    return hasDescription ? 'No part number' : 'No part number · no description';
  }
  if (!isValidPartNumber(partNumber)) return 'Not a valid part number';
  if (!hasDescription) return 'No description';
  return null;
}

export const minLevelOf = (item) => {
  const min = toQty(item?.min_stock_level);
  return min > 0 ? min : null;
};

export const lowThresholdOf = (item) => minLevelOf(item) ?? DEFAULT_MIN_LEVEL;

export const onHandOf = (item) => toQty(item?.on_hand_qty);
export const reservedOf = (item) => toQty(item?.reserved_qty);
export const availableOf = (item) => {
  if (item?.available_qty !== undefined && item?.available_qty !== null) {
    return Math.max(0, toQty(item.available_qty));
  }
  return Math.max(0, onHandOf(item) - reservedOf(item));
};

/**
 * Stock state — one of in_stock | low | reserved | ran_out | never_stocked.
 * "Ran out" needs history (it had stock and now has none); a catalog item that never moved is
 * "Never stocked" — information, not an alert. `movedParts` is a fallback Set of part numbers
 * with stock movements (see stockMovedParts) for API rows that do not carry `has_movements`.
 */
export function stockStatus(item, movedParts) {
  const onHand = onHandOf(item);
  if (onHand <= 0) {
    const hasHistory = item?.has_movements ?? (movedParts ? movedParts.has(item?.part_number) : false);
    return hasHistory ? 'ran_out' : 'never_stocked';
  }
  const available = availableOf(item);
  if (available <= 0) return 'reserved';
  if (available < lowThresholdOf(item)) return 'low';
  return 'in_stock';
}

export const STOCK_STATUS_LABEL = {
  in_stock: 'In stock',
  low: 'Low',
  reserved: 'Reserved',
  ran_out: 'Ran out',
  never_stocked: 'Never stocked'
};

export const STOCK_STATUS_TONE = {
  in_stock: 'green',
  low: 'amber',
  reserved: 'blue',
  ran_out: 'red',
  never_stocked: 'gray'
};

/**
 * Stock tab views (segmented control). 'Reserved' (anything with an active booking) is only shown
 * while something is reserved or when the Dashboard's "Active bookings" card opens it.
 */
export const STOCK_VIEWS = [
  { key: 'has', label: 'Has stock' },
  { key: 'low', label: 'Low' },
  { key: 'reserved', label: 'Reserved', optional: true },
  { key: 'ran_out', label: 'Ran out' },
  { key: 'never', label: 'Never stocked' },
  { key: 'all', label: 'All' }
];

export function stockMatchesView(item, view, movedParts) {
  const status = stockStatus(item, movedParts);
  switch (view) {
    case 'has': return onHandOf(item) > 0;
    case 'low': return status === 'low';
    case 'reserved': return reservedOf(item) > 0;
    case 'ran_out': return status === 'ran_out';
    case 'never': return status === 'never_stocked';
    default: return true;
  }
}

/** Where an item physically is: [{ location, qty }] for locations holding stock. */
export function stockLocations(item) {
  const quantities = item?.location_quantities || {};
  return Object.entries(quantities)
    .map(([location, qty]) => ({ location, qty: toQty(qty) }))
    .filter((entry) => entry.qty > 0)
    .sort((left, right) => right.qty - left.qty);
}

/* --------------------------------------------------------------------- BOQ lines */

export const BOQ_STATUS_LABEL = {
  not_booked: 'Not booked',
  booked: 'Booked',
  partial: 'Partial',
  delivered: 'Delivered'
};

export const BOQ_STATUS_TONE = {
  not_booked: 'gray',
  booked: 'blue',
  partial: 'amber',
  delivered: 'green'
};

/**
 * BOQ line state from what was BOOKED and DELIVERED (not delivered alone):
 * Delivered when nothing remains · Partial when some was delivered, or only part of the remaining
 * quantity is booked · Booked when all of the remaining quantity is reserved · else Not booked.
 */
export function boqStatus({ boq, booked, delivered, remaining }) {
  const boqQty = toQty(boq);
  const bookedQty = toQty(booked);
  const deliveredQty = toQty(delivered);
  const remainingQty = remaining === undefined || remaining === null
    ? Math.max(0, boqQty - deliveredQty)
    : toQty(remaining);
  if (boqQty > 0 && remainingQty <= 0) return 'delivered';
  if (deliveredQty > 0) return 'partial';
  if (bookedQty > 0 && bookedQty >= remainingQty) return 'booked';
  if (bookedQty > 0) return 'partial';
  return 'not_booked';
}

/** '6,703 pcs' or '6,500 pcs · 203 m' — never adds pieces to metres. */
export function formatUnitTotals(totals) {
  const entries = Object.entries(totals || {}).filter(([, qty]) => toQty(qty) > 0);
  if (!entries.length) return `0 pcs`;
  return entries
    .sort((left, right) => (left[0] === 'pcs' ? -1 : right[0] === 'pcs' ? 1 : right[1] - left[1]))
    .map(([unit, qty]) => `${formatQty(qty)} ${unit}`)
    .join(' · ');
}

export function sumByUnit(rows, getQty, getUnit = (row) => row.unit) {
  return rows.reduce((totals, row) => {
    const unit = String(getUnit(row) || 'pcs').trim() || 'pcs';
    totals[unit] = (totals[unit] || 0) + toQty(getQty(row));
    return totals;
  }, {});
}

/* --------------------------------------------------------------------- movements */

export const MOVEMENT_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'GRN', label: 'GRN' },
  { key: 'transfer', label: 'Transfer' },
  { key: 'site-dn', label: 'Site DN' },
  { key: 'testing', label: 'Lab use' },
  { key: 'return', label: 'Return' },
  { key: 'adjustment', label: 'Adjust' }
];

/**
 * A delivery straight from the supplier to a site (procurement DN with no source store, logged as
 * movement_type 'GRN' + delivery_type 'to_site'). It is recorded in the ledger but never touched
 * stock, so it must not make an item "Ran out" nor count as stock going out.
 * Same rule as DIRECT_TO_SITE_MOVEMENT in backend/routes/inventory.py.
 */
export function isDirectToSite(movement) {
  if (movement?.delivery_type !== 'to_site') return false;
  return movement?.movement_type !== 'issue' || !String(movement?.source_location ?? '').trim();
}

export const movementTouchesStock = (movement) => !isDirectToSite(movement);

/** Part numbers whose stock was ever changed by a movement (fallback for `has_movements`). */
export const stockMovedParts = (movements = []) => new Set(
  movements.filter(movementTouchesStock).map((movement) => movement.part_number)
);

/** GRN | transfer | site-dn | testing | return | adjustment */
export function movementKind(movement) {
  if (movement?.delivery_type === 'to_site' || movement?.movement_type === 'issue') return 'site-dn';
  return movement?.movement_type || 'adjustment';
}

export const MOVEMENT_KIND_LABEL = {
  GRN: 'GRN',
  transfer: 'Transfer',
  'site-dn': 'Site DN',
  testing: 'Lab use',
  return: 'Return',
  adjustment: 'Adjustment'
};

export const MOVEMENT_KIND_TONE = {
  GRN: 'green',
  transfer: 'blue',
  'site-dn': 'violet',
  testing: 'teal',
  return: 'navy',
  adjustment: 'gray'
};

export const movementMatchesFilter = (movement, filter) => (
  filter === 'all' || !filter || movementKind(movement) === filter
);

/**
 * +1 stock comes in, -1 goes out, 0 when stock does not change in total: a transfer between stores,
 * or a Site DN delivered straight from the supplier (never in stock).
 */
export function movementDirection(movement) {
  const kind = movementKind(movement);
  if (kind === 'GRN' || kind === 'return') return 1;
  if (kind === 'site-dn') return isDirectToSite(movement) ? 0 : -1;
  if (kind === 'testing') return -1;
  if (kind === 'adjustment') return toQty(movement?.qty) < 0 ? -1 : 1;
  return 0;
}

export const signedQty = (movement) => {
  const direction = movementDirection(movement);
  const qty = Math.abs(toQty(movement?.qty));
  return direction === 0 ? qty : direction * qty;
};

export const timeOf = (value) => {
  const parsed = parseDateValue(value);
  if (!parsed) return 0;
  return parsed.dateOnly ? Date.UTC(parsed.y, parsed.m - 1, parsed.d) : parsed.date.getTime();
};

const movementTime = (movement) => timeOf(movement?.date || movement?.created_at);

/**
 * Which ledger document a line belongs to — the SAME rule as movement_document_key() in
 * backend/routes/inventory.py, which refuses Reverse / Link PO / Attach on lines of two documents:
 *  - lines recorded together share a `document_id` (one Record movement, one reversal);
 *  - older lines: same kind + reference + delivery note (or project) on the same (UTC) day, so a GRN 916
 *    and a Site DN 916 for another project — or a DN number typed again next month — stay apart;
 *  - an adjustment, a reversal or a line without a reference stands alone: an adjustment's reference
 *    is free text (older clients put the reason there), so two 'Count correction's are never one document.
 */
export function movementGroupKey(movement, index = 0) {
  const documentId = String(movement?.document_id ?? '').trim();
  if (documentId) return `doc|${documentId}`;
  const reference = String(movement?.reference ?? '').trim();
  const kind = movementKind(movement);
  if (!reference || kind === 'adjustment' || movement?.reverses_movement_id) return `single|${movement?.id ?? index}`;
  const scope = movement?.delivery_note_id || movement?.project_id || '';
  const day = String(movement?.date || movement?.created_at || '').slice(0, 10);
  return `${kind}|${reference}|${scope}|${day}`;
}

/** A fresh id for the lines of one Record movement (letters, digits and '-', as the backend checks). */
export function newDocumentId() {
  const cryptoApi = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  const part = () => Math.random().toString(16).slice(2, 10).padEnd(8, '0');
  return `${part()}-${part().slice(0, 4)}-${part().slice(0, 4)}-${part().slice(0, 4)}-${part()}${part().slice(0, 4)}`;
}

export function groupMovements(movements = []) {
  const groups = new Map();
  movements.forEach((movement, index) => {
    const key = movementGroupKey(movement, index);
    if (!groups.has(key)) {
      groups.set(key, { key, kind: movementKind(movement), reference: String(movement?.reference ?? '').trim(), movements: [] });
    }
    groups.get(key).movements.push(movement);
  });
  return [...groups.values()]
    .map((group) => {
      const sorted = [...group.movements].sort((left, right) => movementTime(right) - movementTime(left));
      const first = sorted[0] || {};
      const users = [...new Set(sorted.map((movement) => movement.user).filter(Boolean))];
      const sources = [...new Set(sorted.map((movement) => movement.source_location).filter(Boolean))];
      const destinations = [...new Set(sorted.map((movement) => movement.destination_location).filter(Boolean))];
      const qtyTotal = sorted.reduce((sum, movement) => sum + Math.abs(toQty(movement.qty)), 0);
      // Lines that leave stock unchanged (transfers, direct-to-site) carry no sign.
      const signedTotal = sorted.reduce((sum, movement) => sum + (movementDirection(movement) === 0 ? 0 : signedQty(movement)), 0);
      const neutral = sorted.every((movement) => movementDirection(movement) === 0);
      const direct = sorted.length > 0 && sorted.every(isDirectToSite);
      return {
        ...group,
        movements: sorted,
        date: first.date || first.created_at || '',
        time: movementTime(first),
        user: users.length > 1 ? 'Multiple people' : users[0] || '',
        users,
        source: sources.length > 1 ? 'Several stores' : sources[0] || '',
        destination: destinations.length > 1 ? 'Several stores' : destinations[0] || '',
        projectId: first.project_id || '',
        lines: sorted.length,
        qtyTotal,
        signedTotal: neutral ? qtyTotal : signedTotal,
        neutral,
        direct,
        direction: movementDirection(first),
        notes: [...new Set(sorted.map((movement) => String(movement.notes || '').trim()).filter(Boolean))]
      };
    })
    .sort((left, right) => right.time - left.time);
}

const REFERENCE_PREFIX = { GRN: 'GRN', 'site-dn': 'DN', transfer: 'TRF', testing: 'LAB', return: 'RET' };

/** 'GRN 945', 'DN 1023', 'Transfer' (no reference), 'Adjustment'. */
export function documentLabel(group) {
  if (group.kind === 'adjustment') return 'Adjustment';
  if (group.reference) {
    const prefix = REFERENCE_PREFIX[group.kind];
    const reference = group.reference;
    if (prefix && reference.toUpperCase().startsWith(prefix)) return reference;
    return prefix ? `${prefix} ${reference}` : reference;
  }
  return MOVEMENT_KIND_LABEL[group.kind] || 'Movement';
}

/** 'Supplier → Abu Dhabi Store', 'Dubai Store → Nobu Hotel', 'At Abu Dhabi Store'. */
export function documentRoute(group, projectName = '') {
  const source = group.source || '—';
  const destination = group.destination || '—';
  switch (group.kind) {
    case 'GRN': return `Supplier → ${destination}`;
    case 'site-dn': return group.direct
      ? `Supplier → ${projectName || 'Site'} · direct, not from stock`
      : `${source} → ${projectName || 'Site'}`;
    case 'transfer': return `${source} → ${destination}`;
    case 'testing': return `${source} → Lab use`;
    case 'return': return `Returned to ${destination}`;
    default: return `At ${group.source || group.destination || '—'}`;
  }
}

/** 'into Abu Dhabi Store', 'from Dubai Store to Nobu Hotel'. */
export function documentFlowPhrase(group, projectName = '') {
  switch (group.kind) {
    case 'GRN': return `into ${group.destination || 'stock'}`;
    case 'site-dn': return group.direct
      ? `direct from supplier to ${projectName || 'site'}`
      : `from ${group.source || 'stock'} to ${projectName || 'site'}`;
    case 'transfer': return `${group.source || '—'} → ${group.destination || '—'}`;
    case 'testing': return `used from ${group.source || 'stock'}`;
    case 'return': return `back into ${group.destination || 'stock'}`;
    default: return `at ${group.source || group.destination || 'stock'}`;
  }
}

/** Quantity that entered (+) and left (−) stock — transfers and direct-to-site lines count in neither. */
export function stockInOut(movements = []) {
  return movements.reduce((totals, movement) => {
    const direction = movementDirection(movement);
    const qty = Math.abs(toQty(movement?.qty));
    if (direction > 0) totals.in += qty;
    if (direction < 0) totals.out += qty;
    return totals;
  }, { in: 0, out: 0 });
}

export const pcsLabel = (qty) => `${formatQty(qty)} ${toQty(qty) === 1 ? 'pc' : 'pcs'}`;

/** Movement period chip. */
export const MOVEMENT_PERIODS = [
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: '3m', label: 'Last 3 months', days: 92 },
  { value: '6m', label: 'Last 6 months', days: 183 },
  { value: '12m', label: 'Last 12 months', days: 365 }
];

export function withinPeriod(value, period, now) {
  if (!period) return true;
  const option = MOVEMENT_PERIODS.find((entry) => entry.value === period);
  if (!option) return true;
  const days = ageDays(value, { now });
  return days !== null && days <= option.days;
}

export const shortDate = (value) => formatDate(value, { year: 'auto' });

/* --------------------------------------------------------------------- people */

const AVATAR_TONES = ['var(--brand)', 'var(--status-green)', 'var(--status-amber)', 'var(--status-violet)', 'var(--status-teal)', 'var(--status-blue)'];

export function personInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

export function personColor(name) {
  const text = String(name || '');
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) hash = ((hash * 31) + text.charCodeAt(index)) >>> 0;
  return AVATAR_TONES[hash % AVATAR_TONES.length];
}

/* --------------------------------------------------------------------- reorder (Dashboard idea 3) */

/**
 * Low-stock items with what to order: Reorder = threshold − available (the item's Min level, or the
 * default 5 when none is set). Real part numbers first, then the biggest shortfall.
 */
export function reorderRows(stock = [], movedParts, siteDemandOf = () => 0) {
  return stock
    .filter((item) => stockStatus(item, movedParts) === 'low')
    .map((item) => {
      const available = availableOf(item);
      const min = minLevelOf(item);
      const threshold = min ?? DEFAULT_MIN_LEVEL;
      const demand = toQty(siteDemandOf(item));
      return {
        item,
        available,
        min,
        threshold,
        demand,
        short: Math.max(0, demand - available),
        reorder: Math.max(0, threshold - available),
        issue: itemDataIssue(item)
      };
    })
    .sort((left, right) => (
      Number(Boolean(left.issue)) - Number(Boolean(right.issue))
      || right.short - left.short
      || left.available - right.available
      || String(left.item.part_number).localeCompare(String(right.item.part_number))
    ));
}

/* --------------------------------------------------------------------- bookings (Dashboard idea 7) */

/** Days past the needed-by date (> 0 = late), or null when there is no date. */
export function bookingLateDays(reservation, now) {
  const days = ageDays(reservation?.needed_by_date, now ? { now } : undefined);
  return days === null ? null : days;
}

/* --------------------------------------------------------------------- catalog duplicates */

/** Same rule as normalize_part_key() in the backend: case, spaces and punctuation ignored. */
export const partKey = (value) => String(value ?? '').toUpperCase().replace(/[^A-Z0-9]+/g, '');

/** { key → [items] } for part numbers that appear more than once (valid part numbers only). */
export function duplicateGroupsByKey(items = []) {
  const groups = new Map();
  items.forEach((item) => {
    const key = partKey(item.part_number);
    if (!isValidPartNumber(key)) return;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });
  return new Map([...groups.entries()].filter(([, list]) => list.length > 1));
}

/**
 * True when every item carries exactly the same part number (trimmed) — a real duplicate that shares stock.
 * 'SAE-UE-MS-CSA-WE' vs 'SAE-UE-MS-CSAWE' only match once '-' and spaces are ignored: maybe two different parts.
 */
export function samePartNumbers(items = []) {
  const numbers = new Set(items.map((item) => String(item?.part_number ?? '').trim()));
  return items.length > 1 && numbers.size === 1;
}

/**
 * The groups the duplicates review lists, in order: same part number ('exact'), part numbers that match only
 * ignoring '-' and spaces ('normalized'), then similar descriptions ('similar', minus groups already listed).
 * Only 'exact' groups start ticked for merging. The toast after "Find duplicates" counts these same groups.
 */
export function duplicateReviewGroups(keyGroups = [], similarGroups = []) {
  const keyed = keyGroups.filter((items) => items.length > 1);
  const keyedIds = new Set(keyed.flat().map((item) => item.id));
  const byKey = keyed.map((items) => ({
    key: `exact:${partKey(items[0]?.part_number)}`,
    kind: samePartNumbers(items) ? 'exact' : 'normalized',
    items
  }));
  const similar = (similarGroups || [])
    .map((group, index) => ({ key: `similar:${group.group_id || index}`, kind: 'similar', items: group.items || [] }))
    // A similar-description group that is exactly a part-number group is shown once, above.
    .filter((group) => group.items.length > 1 && !group.items.every((item) => keyedIds.has(item.id)));
  return [...byKey.filter((group) => group.kind === 'exact'), ...byKey.filter((group) => group.kind === 'normalized'), ...similar];
}

/* --------------------------------------------------------------------- sites BOQ coverage (idea 3) */

/**
 * Can the stores cover what a BOQ line still needs? 'covered' / 'short' (nothing left = 'done').
 * `available` is the FREE stock (on hand minus every booking). Stock already booked for the line's own
 * projects is theirs to issue, so it counts too — `booked` is that booking, at most what is remaining
 * per project (see bookedForRemaining) and never more than is physically on hand (`onHand`).
 * Returns { state, short, have, booked } — have = free + usable booked.
 */
export function boqCoverage(remaining, available, booked = 0, onHand = null) {
  const need = toQty(remaining);
  if (need <= 0) return { state: 'done', short: 0, have: 0, booked: 0 };
  const free = Math.max(0, toQty(available));
  let own = Math.max(0, toQty(booked));
  if (onHand !== null && onHand !== undefined) own = Math.min(own, Math.max(0, toQty(onHand)));
  own = Math.min(own, need);
  const have = free + own;
  return have >= need
    ? { state: 'covered', short: 0, have, booked: own }
    : { state: 'short', short: need - have, have, booked: own };
}

/** Booked stock that serves a BOQ line: per project, at most what that project still needs. */
export function bookedForRemaining(projectDetails = []) {
  return projectDetails.reduce(
    (sum, detail) => sum + Math.min(Math.max(0, toQty(detail.booked_qty)), Math.max(0, toQty(detail.remaining_qty))),
    0
  );
}

/**
 * What the sites still need per part and nobody has booked yet: Σ over projects of
 * (BOQ − delivered − booked). The ONE "site demand" number used by the Dashboard reorder list and the
 * Stock tab, and the same lines Sites BOQ lists ("Short" there = this demand − free stock).
 */
export function boqDemandByPart(sitesBoq = []) {
  const demand = new Map();
  sitesBoq.forEach((row) => {
    const partNumber = row?.part_number;
    if (!partNumber) return;
    const open = (row.project_details || []).reduce((sum, detail) => {
      const unbooked = detail.unbooked_qty !== undefined && detail.unbooked_qty !== null
        ? toQty(detail.unbooked_qty)
        : toQty(detail.remaining_qty) - toQty(detail.booked_qty);
      return sum + Math.max(0, unbooked);
    }, 0);
    if (open > 0) demand.set(partNumber, (demand.get(partNumber) || 0) + open);
  });
  return demand;
}

/**
 * Active bookings of one project for one part (optionally at one store): a Site DN for that project may
 * use them — the backend fulfils the booking when it issues (create_movement).
 */
export function projectBookedQty(reservations = [], { partNumber, projectId, location } = {}) {
  if (!partNumber || !projectId) return 0;
  return reservations
    .filter((reservation) => (
      (reservation.status || 'Active') === 'Active'
      && reservation.part_number === partNumber
      && reservation.project_id === projectId
      && (!location || (String(reservation.location || '').trim() || INVENTORY_LOCATIONS[0]) === location)
    ))
    .reduce((sum, reservation) => sum + Math.max(0, toQty(reservation.qty_reserved)), 0);
}

/* --------------------------------------------------------------------- site demand vs stock (17 #9) */

/**
 * Per project: open BOQ lines, what remains, how much of it stock could cover today and the shortage.
 * Each project is measured against ALL free stock ("could the stores cover this project?"); what is already
 * booked for the project counts as covered. Sorted by shortage, biggest first. (The server's Send shortage
 * plan shares free stock between projects, earliest end date first — its preview is the one that is sent.)
 */
export function siteDemandByProject(sitesBoq = [], stock = [], projects = []) {
  const freeByPart = new Map(stock.map((item) => [item.part_number, availableOf(item)]));
  const projectById = new Map(projects.map((project) => [project.id, project]));
  const byProject = new Map();
  sitesBoq.forEach((row) => {
    const free = Math.max(0, toQty(freeByPart.get(row.part_number)));
    const unit = String(row.unit || 'pcs').trim() || 'pcs';
    (row.project_details || []).forEach((detail) => {
      const remaining = Math.max(0, toQty(detail.remaining_qty));
      if (remaining <= 0 || !detail.project_id) return;
      const booked = Math.min(remaining, Math.max(0, toQty(detail.booked_qty)));
      const fromStock = Math.min(remaining - booked, free);
      const project = projectById.get(detail.project_id);
      if (!byProject.has(detail.project_id)) {
        byProject.set(detail.project_id, {
          id: detail.project_id,
          name: project?.name || detail.project_name || 'Unknown project',
          system: String(project?.system_type || '').trim(),
          lines: 0,
          remaining: 0,
          covered: 0,
          short: 0,
          shortLines: 0,
          units: {}
        });
      }
      const entry = byProject.get(detail.project_id);
      entry.lines += 1;
      entry.remaining += remaining;
      entry.covered += booked + fromStock;
      entry.short += remaining - booked - fromStock;
      if (remaining - booked - fromStock > 0) entry.shortLines += 1;
      entry.units[unit] = (entry.units[unit] || 0) + remaining;
    });
  });
  return [...byProject.values()]
    .map((entry) => ({ ...entry, coveredPct: entry.remaining > 0 ? (entry.covered / entry.remaining) * 100 : 100 }))
    .sort((left, right) => right.short - left.short || right.remaining - left.remaining || left.name.localeCompare(right.name));
}

/**
 * Sites BOQ lines still to deliver: one per part number, however many projects need it — the unit of the
 * Dashboard's "Sites waiting" KPI and of Sites BOQ ("BOQ lines"). siteDemandByProject counts per project.
 */
export function openBoqPartCount(sitesBoq = []) {
  return new Set(sitesBoq
    .filter((row) => row?.part_number && (row.project_details || []).some((detail) => detail.project_id && toQty(detail.remaining_qty) > 0))
    .map((row) => row.part_number)).size;
}

/* --------------------------------------------------------------------- Site DN tracking (17 #8 / 21 #9) */

export const SITE_DN_OVERDUE_HOURS = 48;

/** Every line of the document (or merged DN) was reversed: nothing of it is left. */
export function isFullyReversed(group) {
  const lines = (group?.movements || []).filter((movement) => !movement.reverses_movement_id);
  return lines.length > 0 && lines.every((movement) => movement.reversed_by_movement_id);
}

/**
 * Where a Site DN document stands: { state: 'received' | 'overdue' | 'dispatched', dnId, … } — or null for
 * documents that are not tracked (not a Site DN, delivered straight from the supplier, recorded before
 * tracking existed) and for DNs that were reversed before the site signed (nothing is left to receive).
 * Overdue = still not confirmed 48 hours after it left the store.
 */
export function siteDnReceipt(group, now = Date.now()) {
  if (!group || group.kind !== 'site-dn' || group.direct) return null;
  const line = group.movements.find((movement) => movement.delivery_note_id && movement.dn_receipt_status) || null;
  if (!line) return null;
  if (line.dn_receipt_status === 'Reversed') return null;
  if (line.dn_receipt_status !== 'Received' && isFullyReversed(group)) return null;
  const base = { dnId: line.delivery_note_id, dispatchedAt: line.dn_dispatched_at || group.date };
  if (line.dn_receipt_status === 'Received') {
    return {
      ...base,
      state: 'received',
      receivedAt: line.dn_received_at,
      receivedBy: line.dn_received_by_name,
      confirmedBy: line.dn_confirmed_by,
      notes: line.dn_receipt_notes,
      signatureUrl: line.dn_signature_url,
      photoUrl: line.dn_photo_url
    };
  }
  const sent = timeOf(base.dispatchedAt);
  const hours = sent ? Math.max(0, (toQty(now) - sent) / 3600000) : 0;
  return { ...base, state: hours >= SITE_DN_OVERDUE_HOURS ? 'overdue' : 'dispatched', hours };
}

const deliveryNoteIdOf = (group) => group.movements.find((movement) => movement.delivery_note_id)?.delivery_note_id || '';

/** Several ledger documents of one delivery note as one: all their lines, the newest document's key. */
function mergeDnDocuments(dnKey, groups) {
  const [latest] = groups; // groupMovements order: newest first
  const earliest = groups[groups.length - 1];
  const movements = groups.flatMap((group) => group.movements);
  const users = [...new Set(groups.flatMap((group) => group.users))];
  const sources = [...new Set(movements.map((movement) => movement.source_location).filter(Boolean))];
  const merged = groups.length === 1 ? { ...latest } : {
    ...latest,
    movements,
    users,
    user: users.length > 1 ? 'Multiple people' : users[0] || '',
    source: sources.length > 1 ? 'Several stores' : sources[0] || '',
    lines: movements.length,
    qtyTotal: groups.reduce((sum, group) => sum + group.qtyTotal, 0),
    signedTotal: groups.reduce((sum, group) => sum + group.signedTotal, 0),
    notes: [...new Set(groups.flatMap((group) => group.notes))]
  };
  return {
    ...merged,
    dnKey,
    dnId: deliveryNoteIdOf(latest),
    documentKeys: groups.map((group) => group.key),
    firstDate: earliest.date,
    reversed: isFullyReversed(merged)
  };
}

/**
 * One entry per Site DN — the delivery note, not the ledger document: items added to a DN number that is
 * still on its way, in a second Record-movement session, are a second document of the SAME DN. Newest
 * first; deliveries straight from the supplier are left out. `reversed` = every line was reversed.
 */
export function siteDeliveryNotes(movements = []) {
  const byDn = new Map();
  groupMovements(movements).forEach((group) => {
    if (group.kind !== 'site-dn' || group.direct) return;
    const dnId = deliveryNoteIdOf(group);
    const key = dnId ? `dn|${dnId}` : group.key;
    if (!byDn.has(key)) byDn.set(key, []);
    byDn.get(key).push(group);
  });
  return [...byDn.entries()].map(([key, groups]) => mergeDnDocuments(key, groups));
}

/** The whole Site DN a ledger document belongs to (all its documents merged) — or the document itself. */
export function siteDeliveryNoteOf(movements = [], group) {
  if (!group) return null;
  const dnId = group.kind === 'site-dn' ? deliveryNoteIdOf(group) : '';
  if (!dnId) return group;
  return siteDeliveryNotes(movements).find((dn) => dn.dnId === dnId) || group;
}

/** Site DNs (one per delivery note) with their receipt state, newest first — tracked, not reversed. */
export function trackedSiteDns(movements = [], now = Date.now()) {
  return siteDeliveryNotes(movements)
    .map((group) => ({ group, receipt: siteDnReceipt(group, now) }))
    .filter((entry) => entry.receipt);
}

/** When a Site DN left the store: its tracked dispatch time, else its first document's date. */
export function siteDnSentAt(dn) {
  return dn?.movements?.find((movement) => movement.dn_dispatched_at)?.dn_dispatched_at || dn?.firstDate || dn?.date || '';
}

/* --------------------------------------------------------------------- stock flow (21 #7) */

export const FLOW_SOURCES = [
  { key: 'suppliers', label: 'Suppliers (GRN)', filter: 'GRN' },
  { key: 'returns', label: 'Returns from sites', filter: 'return' },
  { key: 'found', label: 'Adjustments +', filter: 'adjustment' }
];

export const FLOW_OUTS = [
  { key: 'sites', label: 'Sites (Site DN)', filter: 'site-dn' },
  { key: 'lab', label: 'Lab use', filter: 'testing' },
  { key: 'written_off', label: 'Adjustments −', filter: 'adjustment' }
];

/**
 * Where stock came from and went in a set of movements: sources → stores → outs, in pieces and lines.
 * Lines delivered straight from the supplier never touched stock and are left out; a reversed line and its
 * reversal cancel out, so both are left out. Transfers stay between stores (counted on their own).
 */
export function stockFlow(movements = []) {
  const stores = new Map(INVENTORY_LOCATIONS.map((location) => [location, { location, in: 0, out: 0, inLines: 0, outLines: 0 }]));
  const store = (location) => {
    const key = String(location || '').trim() || INVENTORY_LOCATIONS[0];
    if (!stores.has(key)) stores.set(key, { location: key, in: 0, out: 0, inLines: 0, outLines: 0 });
    return stores.get(key);
  };
  const ends = Object.fromEntries([...FLOW_SOURCES, ...FLOW_OUTS].map((entry) => [entry.key, { qty: 0, lines: 0 }]));
  const bands = new Map();
  const transfers = { qty: 0, lines: 0 };
  const band = (from, to, qty) => {
    const key = `${from}→${to}`;
    const entry = bands.get(key) || { key, from, to, qty: 0, lines: 0 };
    entry.qty += qty;
    entry.lines += 1;
    bands.set(key, entry);
  };
  let firstIn = 0;
  let lastOut = 0;
  movements.forEach((movement) => {
    if (isDirectToSite(movement) || movement.reversed_by_movement_id || movement.reverses_movement_id) return;
    const qty = Math.abs(toQty(movement.qty));
    if (!qty) return;
    const kind = movementKind(movement);
    const time = movementTime(movement);
    const into = (source, location) => {
      const entry = store(location);
      entry.in += qty;
      entry.inLines += 1;
      ends[source].qty += qty;
      ends[source].lines += 1;
      band(source, entry.location, qty);
      if (time && (!firstIn || time < firstIn)) firstIn = time;
    };
    const outOf = (location, target) => {
      const entry = store(location);
      entry.out += qty;
      entry.outLines += 1;
      ends[target].qty += qty;
      ends[target].lines += 1;
      band(entry.location, target, qty);
      if (time > lastOut) lastOut = time;
    };
    if (kind === 'GRN') into('suppliers', movement.destination_location);
    else if (kind === 'return') into('returns', movement.destination_location);
    else if (kind === 'site-dn') outOf(movement.source_location, 'sites');
    else if (kind === 'testing') outOf(movement.source_location, 'lab');
    else if (kind === 'adjustment') {
      if (toQty(movement.qty) >= 0) into('found', movement.destination_location || movement.source_location);
      else outOf(movement.source_location || movement.destination_location, 'written_off');
    } else if (kind === 'transfer') {
      transfers.qty += qty;
      transfers.lines += 1;
      store(movement.source_location).out += qty;
      store(movement.destination_location).in += qty;
    }
  });
  const totalIn = FLOW_SOURCES.reduce((sum, entry) => sum + ends[entry.key].qty, 0);
  const totalOut = FLOW_OUTS.reduce((sum, entry) => sum + ends[entry.key].qty, 0);
  return {
    sources: FLOW_SOURCES.map((entry) => ({ ...entry, ...ends[entry.key] })),
    outs: FLOW_OUTS.map((entry) => ({ ...entry, ...ends[entry.key] })),
    stores: [...stores.values()],
    bands: [...bands.values()],
    transfers,
    totalIn,
    totalOut,
    firstIn: firstIn ? new Date(firstIn).toISOString() : '',
    lastOut: lastOut ? new Date(lastOut).toISOString() : ''
  };
}

/* --------------------------------------------------------------------- scan & move (18 #10) */

/** The stock row a scanned / typed code points at: exact part number first, then ignoring case and punctuation. */
export function findStockByCode(stock = [], code = '') {
  const typed = String(code ?? '').trim();
  if (!typed) return null;
  const upper = typed.toUpperCase();
  const exact = stock.find((item) => String(item.part_number ?? '').trim().toUpperCase() === upper);
  if (exact) return exact;
  const key = partKey(typed);
  if (!isValidPartNumber(key)) return null;
  return stock.find((item) => partKey(item.part_number) === key) || null;
}

/** 'Shelf B3' for one store, or '' (bins are kept per store on the stock row). */
export const binOf = (item, location) => String(item?.bins?.[location] ?? '').trim();

/* --------------------------------------------------------------------- paste parsing */

/**
 * Rows pasted from Excel (tab-separated; or 2+ spaces, comma, semicolon). A first row whose cells
 * name the columns ('Part', 'Description', 'Qty'…) maps them; otherwise `defaultColumns` gives the order.
 * Returns [{ field: value }] for the given column keys.
 */
export function parsePastedRows(raw, defaultColumns, aliases = {}) {
  const lines = String(raw || '').split(/\r?\n/).map((line) => line.replace(/\s+$/, '')).filter((line) => line.trim());
  if (!lines.length) return [];
  const split = (line) => (line.includes('\t') ? line.split('\t') : line.split(/\s{2,}|,|;/)).map((cell) => cell.trim());
  const headerCells = split(lines[0]).map((cell) => cell.toLowerCase());
  const findColumn = (cell) => Object.entries(aliases).find(([, names]) => names.some((name) => cell === name || cell.startsWith(name)))?.[0];
  const mapped = headerCells.map(findColumn);
  const isHeader = mapped.filter(Boolean).length >= Math.min(2, defaultColumns.length);
  const columns = isHeader ? mapped : defaultColumns;
  return lines.slice(isHeader ? 1 : 0).map((line) => {
    const cells = split(line);
    const row = {};
    columns.forEach((field, index) => {
      if (field && cells[index] !== undefined && cells[index] !== '') row[field] = cells[index];
    });
    return row;
  }).filter((row) => Object.keys(row).length);
}

/* --------------------------------------------------------------------- misc */

export const plural = (count, singular, pluralLabel = `${singular}s`) => (
  `${formatNumber(count, { maxDecimals: 0 })} ${toQty(count) === 1 ? singular : pluralLabel}`
);

export function paginate(rows, page, pageSize) {
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(Math.max(1, page), pageCount);
  const start = (safePage - 1) * pageSize;
  return {
    page: safePage,
    pageCount,
    start,
    end: Math.min(rows.length, start + pageSize),
    rows: rows.slice(start, start + pageSize)
  };
}

export const EMPTY_FILTER_VALUE = '__none__';
